/**
 * Permission codes the close cockpit declares an interest in
 * (`internal/close/permissions.go`, design C3 §6.1).
 *
 * `close.task.complete` ⊗ `close.run.certify` is a declared segregation-of-duties
 * pair, and §6.1 is explicit that the pair alone is **not** the control: one
 * person may hold both legitimately in a large finance team, and the real rule
 * is about a *particular run* — `Certify` refuses when every task sign-off on the
 * run names the certifier. So these screens never treat holding both permissions
 * as an error; they surface the server's refusal, which is the thing that knows.
 *
 * `close` routes carry `Module: ""` — a close cockpit is not a purchasable
 * module (§6), so no screen here gates on an entitlement.
 */

export const CLOSE_PERMISSIONS = {
  calendarRead: 'close.calendar.read',
  calendarManage: 'close.calendar.manage',

  runRead: 'close.run.read',
  runManage: 'close.run.manage',

  taskComplete: 'close.task.complete',
  /** Accept a failing readiness task, with a governed reason, on the record (§4.6). */
  taskAccept: 'close.task.accept',
  /** Run depreciation / cost / ECL / allocation from the cockpit. */
  actionRun: 'close.action.run',
  /** Step-up MFA server-side. */
  runCertify: 'close.run.certify',
  evidenceRead: 'close.evidence.read',
  /** The certification register (FSD 2.2.8.5). */
  certificationRead: 'close.certification.read',
  /** Sub-ledger to General Ledger reconciliation (FSD 2.2.8, V02). */
  reconciliationRead: 'close.reconciliation.read',
  /** Financial statement sets (FSD FIN-F09 Generate and Publish). */
  statementsRead: 'close.statements.read',
  /** Read anomaly findings and the trial balance with anomaly flags (FSD 2.2.8.9, 2.2.8.10). */
  anomalyRead: 'close.anomaly.read',
  /**
   * Disposition a finding (explained | corrected | false positive, with a reason). The server
   * also refuses anybody who prepared or posted what the finding flags (ERR_SOD_VIOLATION).
   */
  anomalyDisposition: 'close.anomaly.disposition',
  /** The trial balance with anomaly flags also reads the general ledger's own trial balance. */
  glReportRead: 'gl.report.read',

  /** Read companies for the board and the calendar pickers. */
  companyRead: 'org.company.read',
  /** Read fiscal periods: a run is opened against one. */
  periodRead: 'org.period.read',
  /** Read positions for the owner pickers on calendars and templates. */
  positionRead: 'org.position.read',
} as const

export type ClosePermission = (typeof CLOSE_PERMISSIONS)[keyof typeof CLOSE_PERMISSIONS]
